/**
 * Notification Service
 * Handles email notifications for expense workflow events
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */

import { generateClient } from 'aws-amplify/api';
import { User, Expense } from '../types';

// Lazy initialize client to avoid "Amplify not configured" error
// `any`, as in leave.ts: the generated type sends TypeScript 5+ (the mobile app) into "excessive
// stack depth" (TS2321), and nothing here relies on it.
let client: any = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

export interface NotificationRequest {
  type: NotificationType;
  expense: Expense;
  recipient: User;
  sender?: User;
  comments?: string;
}

export enum NotificationType {
  EXPENSE_SUBMITTED = 'EXPENSE_SUBMITTED',
  EXPENSE_APPROVED = 'EXPENSE_APPROVED',
  EXPENSE_REJECTED = 'EXPENSE_REJECTED',
  EXPENSE_REIMBURSED = 'EXPENSE_REIMBURSED'
}

export interface EmailTemplate {
  subject: string;
  htmlBody: string;
  textBody: string;
}

export class NotificationService {
  /**
   * Send notification for expense workflow events
   */
  static async sendNotification(request: NotificationRequest): Promise<void> {
    try {
      const mutation = `
        mutation SendNotification($input: NotificationInput!) {
          sendNotification(input: $input) {
            success
            messageId
            error
          }
        }
      `;

      const variables = {
        input: {
          type: request.type,
          expenseId: request.expense.id,
          recipientEmail: request.recipient.email,
          recipientName: request.recipient.name,
          senderName: request.sender?.name,
          expenseTitle: request.expense.title,
          expenseAmount: request.expense.amount,
          expenseDate: request.expense.date,
          expenseStatus: request.expense.status,
          comments: request.comments
        }
      };

      const response = await getClient().graphql({
        query: mutation,
        variables
      }) as any;

      if (!response.data.sendNotification.success) {
        throw new Error(response.data.sendNotification.error || 'Failed to send notification');
      }

      console.log('Notification sent successfully:', response.data.sendNotification.messageId);
    } catch (error) {
      console.error('Failed to send notification:', error);
      throw error;
    }
  }

  /**
   * Send expense submission notification to manager
   */
  static async notifyExpenseSubmitted(expense: Expense, manager: User, comments?: string): Promise<void> {
    await this.sendNotification({
      type: NotificationType.EXPENSE_SUBMITTED,
      expense,
      recipient: manager,
      comments
    });
  }

  /**
   * Send approval notification to expense submitter
   */
  static async notifyExpenseApproved(expense: Expense, submitter: User, approver: User, comments?: string): Promise<void> {
    await this.sendNotification({
      type: NotificationType.EXPENSE_APPROVED,
      expense,
      recipient: submitter,
      sender: approver,
      comments
    });
  }

  /**
   * Send rejection notification to expense submitter
   */
  static async notifyExpenseRejected(expense: Expense, submitter: User, approver: User, comments?: string): Promise<void> {
    await this.sendNotification({
      type: NotificationType.EXPENSE_REJECTED,
      expense,
      recipient: submitter,
      sender: approver,
      comments
    });
  }

  /**
   * Send reimbursement notification to expense submitter
   */
  static async notifyExpenseReimbursed(expense: Expense, submitter: User, comments?: string): Promise<void> {
    await this.sendNotification({
      type: NotificationType.EXPENSE_REIMBURSED,
      expense,
      recipient: submitter,
      comments
    });
  }

  /**
   * Generate email templates for different notification types
   */
  static generateEmailTemplate(type: NotificationType, data: any): EmailTemplate {
    switch (type) {
      case NotificationType.EXPENSE_SUBMITTED:
        return {
          subject: `New Expense Submitted: ${data.expenseTitle}`,
          htmlBody: `
            <h2>New Expense Submitted for Approval</h2>
            <p>Dear ${data.recipientName},</p>
            <p>A new expense has been submitted for your approval:</p>
            <ul>
              <li><strong>Title:</strong> ${data.expenseTitle}</li>
              <li><strong>Amount:</strong> $${data.expenseAmount}</li>
              <li><strong>Date:</strong> ${data.expenseDate}</li>
              <li><strong>Status:</strong> ${data.expenseStatus}</li>
              ${data.comments ? `<li><strong>Comments:</strong> ${data.comments}</li>` : ''}
            </ul>
            <p>Please log in to the expense management system to review and approve this expense.</p>
            <p>Best regards,<br>Expense Management System</p>
          `,
          textBody: `
            New Expense Submitted for Approval

            Dear ${data.recipientName},

            A new expense has been submitted for your approval:
            - Title: ${data.expenseTitle}
            - Amount: $${data.expenseAmount}
            - Date: ${data.expenseDate}
            - Status: ${data.expenseStatus}
            ${data.comments ? `- Comments: ${data.comments}` : ''}

            Please log in to the expense management system to review and approve this expense.
            
            Best regards,
            Expense Management System
          `
        };

      case NotificationType.EXPENSE_APPROVED:
        return {
          subject: `Expense Approved: ${data.expenseTitle}`,
          htmlBody: `
            <h2>Your Expense Has Been Approved</h2>
            <p>Dear ${data.recipientName},</p>
            <p>Your expense has been approved by ${data.senderName}:</p>
            <ul>
              <li><strong>Title:</strong> ${data.expenseTitle}</li>
              <li><strong>Amount:</strong> $${data.expenseAmount}</li>
              <li><strong>Date:</strong> ${data.expenseDate}</li>
              <li><strong>Status:</strong> ${data.expenseStatus}</li>
              ${data.comments ? `<li><strong>Comments:</strong> ${data.comments}</li>` : ''}
            </ul>
            <p>Your expense is now moving to the next stage of the approval process.</p>
            <p>Best regards,<br>Expense Management System</p>
          `,
          textBody: `
            Your Expense Has Been Approved
            
            Dear ${data.recipientName},
            
            Your expense has been approved by ${data.senderName}:
            - Title: ${data.expenseTitle}
            - Amount: $${data.expenseAmount}
            - Date: ${data.expenseDate}
            - Status: ${data.expenseStatus}
            ${data.comments ? `- Comments: ${data.comments}` : ''}
            
            Your expense is now moving to the next stage of the approval process.
            
            Best regards,
            Expense Management System
          `
        };

      case NotificationType.EXPENSE_REJECTED:
        return {
          subject: `Expense Rejected: ${data.expenseTitle}`,
          htmlBody: `
            <h2>Your Expense Has Been Rejected</h2>
            <p>Dear ${data.recipientName},</p>
            <p>Your expense has been rejected by ${data.senderName}:</p>
            <ul>
              <li><strong>Title:</strong> ${data.expenseTitle}</li>
              <li><strong>Amount:</strong> $${data.expenseAmount}</li>
              <li><strong>Date:</strong> ${data.expenseDate}</li>
              <li><strong>Status:</strong> ${data.expenseStatus}</li>
              ${data.comments ? `<li><strong>Rejection Reason:</strong> ${data.comments}</li>` : ''}
            </ul>
            <p>Please review the rejection reason and resubmit your expense if appropriate.</p>
            <p>Best regards,<br>Expense Management System</p>
          `,
          textBody: `
            Your Expense Has Been Rejected
            
            Dear ${data.recipientName},
            
            Your expense has been rejected by ${data.senderName}:
            - Title: ${data.expenseTitle}
            - Amount: $${data.expenseAmount}
            - Date: ${data.expenseDate}
            - Status: ${data.expenseStatus}
            ${data.comments ? `- Rejection Reason: ${data.comments}` : ''}
            
            Please review the rejection reason and resubmit your expense if appropriate.
            
            Best regards,
            Expense Management System
          `
        };

      case NotificationType.EXPENSE_REIMBURSED:
        return {
          subject: `Expense Reimbursed: ${data.expenseTitle}`,
          htmlBody: `
            <h2>Your Expense Has Been Reimbursed</h2>
            <p>Dear ${data.recipientName},</p>
            <p>Your expense has been processed and reimbursed:</p>
            <ul>
              <li><strong>Title:</strong> ${data.expenseTitle}</li>
              <li><strong>Amount:</strong> $${data.expenseAmount}</li>
              <li><strong>Date:</strong> ${data.expenseDate}</li>
              <li><strong>Status:</strong> ${data.expenseStatus}</li>
              ${data.comments ? `<li><strong>Comments:</strong> ${data.comments}</li>` : ''}
            </ul>
            <p>The reimbursement will be processed according to your company's payment schedule.</p>
            <p>Best regards,<br>Expense Management System</p>
          `,
          textBody: `
            Your Expense Has Been Reimbursed

            Dear ${data.recipientName},

            Your expense has been processed and reimbursed:
            - Title: ${data.expenseTitle}
            - Amount: $${data.expenseAmount}
            - Date: ${data.expenseDate}
            - Status: ${data.expenseStatus}
            ${data.comments ? `- Comments: ${data.comments}` : ''}

            The reimbursement will be processed according to your company's payment schedule.
            
            Best regards,
            Expense Management System
          `
        };

      default:
        throw new Error(`Unknown notification type: ${type}`);
    }
  }
}